// src/modules/PackageManager/WingetTab.tsx
import React, { useState } from 'react';
import { Plus, Search, Loader2, X } from 'lucide-react';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { addAppFromWinget, WingetAppItem } from '../../components/services/PackageManager';

const WingetTab: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [wingetApps, setWingetApps] = useState<WingetAppItem[]>([{ packageIdentifier: '', packageName: '' }]);

  const handleWingetAppChange = (index: number, field: 'packageIdentifier' | 'packageName', value: string) => {
    const updatedApps = [...wingetApps];
    updatedApps[index][field] = value;
    setWingetApps(updatedApps);
  };

  const addWingetAppRow = () => setWingetApps([...wingetApps, { packageIdentifier: '', packageName: '' }]);

  const removeWingetAppRow = (index: number) => {
    if (wingetApps.length > 1) setWingetApps(wingetApps.filter((_, i) => i !== index));
  };

  const resetWingetForm = () => setWingetApps([{ packageIdentifier: '', packageName: '' }]);

  const handleWingetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validApps = wingetApps.filter((app) => app.packageIdentifier.trim() !== '' && app.packageName.trim() !== '');

    if (validApps.length === 0) {
      addToast('Please provide at least one app with Package Identifier and Name', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await addAppFromWinget({
        appsToAdd: validApps.map((app) => ({
          packageIdentifier: app.packageIdentifier.trim(),
          packageName: app.packageName.trim(),
        })),
      });

      if (response.isSuccess) {
        addToast(`Successfully added ${validApps.length} application(s) from Winget`, 'success');
        resetWingetForm();
      } else {
        addToast(response.message || 'Failed to add application(s)', 'error');
      }
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Failed to add application(s) from Winget', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1C1D1D] p-6 rounded-lg border border-gray-700">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white mb-2">Add from Winget</h2>
        <p className="text-sm text-gray-400">Add multiple applications from the Windows Package Manager (Winget) repository</p>
      </div>

      <form onSubmit={handleWingetSubmit} className="space-y-4">
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {wingetApps.map((app, index) => (
            <div key={index} className="bg-[#2A2A2B] p-4 rounded-lg border border-gray-600 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-400">App {index + 1}</span>
                {wingetApps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWingetAppRow(index)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                    disabled={loading}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Package Identifier <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={app.packageIdentifier}
                    onChange={(e) => handleWingetAppChange(index, 'packageIdentifier', e.target.value)}
                    placeholder="e.g., Microsoft.VisualStudioCode"
                    className="w-full bg-[#1C1D1D] border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm pr-9"
                    disabled={loading}
                  />
                  <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Package Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={app.packageName}
                  onChange={(e) => handleWingetAppChange(index, 'packageName', e.target.value)}
                  placeholder="e.g., Microsoft Visual Studio Code"
                  className="w-full bg-[#1C1D1D] border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  disabled={loading}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addWingetAppRow}
          disabled={loading}
          className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Another App
        </button>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Adding...
              </>
            ) : (
              <>
                <Plus size={18} />
                Add Application(s)
              </>
            )}
          </button>
          <button
            type="button"
            onClick={resetWingetForm}
            disabled={loading}
            className="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default WingetTab;
